import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Calendar, Mail, Paperclip } from "lucide-react";
import { MEETING_ATTACHMENT_KINDS } from "@costadorada/shared";
import { TypedAttachmentField, type TypedAttachment } from "@/components/TypedAttachmentField";
import { RelatedDocuments, type RelatedDocument } from "@/components/RelatedDocuments";
import { canOrganizeMeetings } from "@costadorada/shared";
import { api } from "@/lib/api";
import { formatDate, formatTimeOnly } from "@/lib/cn";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/Badge";
import { Modal } from "@/components/Modal";
import { RulePanel } from "@/components/RulePanel";
import { AdminRecordActions } from "@/components/AdminRecordActions";
import {
  MeetingRecipientPicker,
  useRecipientDirectory,
  defaultGroupsFor,
  invitePayload,
  selectionCount,
  EMPTY_SELECTION,
  type InviteSelection,
} from "@/components/MeetingRecipientPicker";

interface MeetingRow {
  id: string;
  title: string;
  meetingType: "JUNTA" | "ASAMBLEA" | "EXTRAORDINARY";
  scheduledAt: string;
  endTime?: string | null;
  status: "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
  location?: string;
  meetLink?: string | null;
  googleEventId?: string | null;
  _count: { agendaItems: number; resolutions: number; attachments?: number };
  minutes?: { id: string; status: string } | null;
}

const statusVariant: Record<string, "default" | "info" | "warning" | "success" | "danger"> = {
  SCHEDULED: "info",
  IN_PROGRESS: "warning",
  COMPLETED: "success",
  CANCELLED: "danger",
};

export function MeetingsPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editMeeting, setEditMeeting] = useState<MeetingRow | null>(null);
  const [inviteMeeting, setInviteMeeting] = useState<MeetingRow | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const { data: meetings = [], isLoading } = useQuery({
    queryKey: ["meetings"],
    queryFn: async () => (await api.get<MeetingRow[]>("/meetings")).data,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/meetings/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["meetings"] }),
  });

  // Convening a meeting is the administrator's and the Secretary's job; other
  // directors see the list read-only. The server enforces the same rule.
  const canCreate = canOrganizeMeetings(user);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={24} /> {t("meetings.title")}
          </h1>
        </div>
        {canCreate && (
          <button onClick={() => setOpen(true)} className="btn-primary">
            <Plus size={16} /> {t("meetings.create")}
          </button>
        )}
      </div>

      <div className="card p-0 overflow-x-auto">
        {isLoading ? (
          <div className="p-6 text-slate-500">{t("common.loading")}</div>
        ) : meetings.length === 0 ? (
          <div className="p-6 text-slate-500">—</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-2">{t("meetings.title")}</th>
                <th className="text-left px-4 py-2">{t("meetings.scheduledAt")}</th>
                <th className="text-left px-4 py-2">Tipo</th>
                <th className="text-left px-4 py-2">{t("resolutions.status")}</th>
                <th className="text-left px-4 py-2">{t("meetings.minutes")}</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {meetings.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/meetings/${m.id}`} className="font-medium text-primary hover:underline">
                      {m.title}
                    </Link>
                    <div className="text-xs text-slate-500">
                      {m._count.agendaItems} {t("meetings.agenda").toLowerCase()} · {m._count.resolutions} {t("resolutions.title").toLowerCase()}
                      <Link to={`/meetings/${m.id}`} className="inline-flex items-center gap-0.5 ml-2 text-slate-600 hover:text-primary" title={t("meetings.attachments")}>
                        <Paperclip size={11} /> {m._count.attachments ?? 0}
                      </Link>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {formatDate(m.scheduledAt, i18n.language)}
                    {m.endTime && <span className="text-slate-400"> – {formatTimeOnly(m.endTime, i18n.language)}</span>}
                  </td>
                  <td className="px-4 py-3">{m.meetingType}</td>
                  <td className="px-4 py-3"><Badge variant={statusVariant[m.status]}>{m.status}</Badge></td>
                  <td className="px-4 py-3">
                    {m.minutes ? <Badge variant="info">{m.minutes.status}</Badge> : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-3">
                      <Link
                        to={`/meetings/${m.id}`}
                        className="text-slate-600 hover:text-primary text-sm inline-flex items-center gap-1"
                        title={t("meetings.attachments")}
                      >
                        <Paperclip size={14} />
                      </Link>
                      {canCreate && (
                        <button
                          type="button"
                          onClick={() => setInviteMeeting(m)}
                          className="text-slate-600 hover:text-primary text-sm inline-flex items-center gap-1"
                          title={t("meetings.invite")}
                        >
                          <Mail size={14} />
                        </button>
                      )}
                      <AdminRecordActions
                        compact
                        onEdit={() => setEditMeeting(m)}
                        onDelete={() => deleteMutation.mutate(m.id)}
                        entityLabel="la reunión"
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {notice && (
        <div className="fixed bottom-4 right-4 z-50 bg-green-600 text-white text-sm rounded shadow-lg px-4 py-2">
          {notice}
        </div>
      )}

      <CreateMeetingModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(_created, sent) => {
          qc.invalidateQueries({ queryKey: ["meetings"] });
          if (typeof sent === "number") {
            setNotice(t("meetings.invitesSent", { count: sent }));
            setTimeout(() => setNotice(null), 5000);
          }
        }}
      />
      {editMeeting && (
        <CreateMeetingModal
          key={editMeeting.id}
          open
          meeting={editMeeting}
          onClose={() => setEditMeeting(null)}
          onCreated={() => qc.invalidateQueries({ queryKey: ["meetings"] })}
        />
      )}
      {inviteMeeting && (
        <InviteModal key={inviteMeeting.id} meeting={inviteMeeting} onClose={() => setInviteMeeting(null)} />
      )}
    </div>
  );
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

function CreateMeetingModal({
  open,
  meeting,
  onClose,
  onCreated,
}: {
  open: boolean;
  meeting?: MeetingRow | null;
  onClose: () => void;
  /** `sent` is the invitation count when the organizer asked to invite on create. */
  onCreated: (created?: MeetingRow, sent?: number) => void;
}) {
  const { t } = useTranslation();
  const isEdit = !!meeting;
  const [form, setForm] = useState({
    title: meeting?.title ?? "",
    meetingType: (meeting?.meetingType ?? "JUNTA") as "JUNTA" | "ASAMBLEA" | "EXTRAORDINARY",
    scheduledAt: meeting ? toLocalInput(meeting.scheduledAt) : "",
    endTime: meeting?.endTime ? toLocalInput(meeting.endTime) : "",
    location: meeting?.location ?? "",
    // Bug: this always started blank, even editing a meeting that already had a
    // link — so it looked unsaved every time the edit modal was reopened, and a
    // freshly generated link that WAS saved could be silently dropped on the next
    // save if the admin then edited something else without regenerating it.
    meetLink: meeting?.meetLink ?? "",
  });
  // Once the organizer picks a start time, suggest an end an hour later — pre-filled,
  // never silent: they see it and can change it before saving (the actual bug this
  // fixes: previously there was no end-time field at all, just an invisible +1h
  // applied server-side in the .ics invite and the Meet-link duration).
  const suggestEndTime = (scheduledAt: string) => {
    if (!scheduledAt) return "";
    const start = new Date(scheduledAt);
    if (Number.isNaN(start.getTime())) return "";
    return toLocalInput(new Date(start.getTime() + 60 * 60 * 1000).toISOString());
  };
  const [inviteOnCreate, setInviteOnCreate] = useState(false);
  const [selection, setSelection] = useState<InviteSelection>({
    ...EMPTY_SELECTION,
    groups: defaultGroupsFor(meeting?.meetingType ?? "JUNTA"),
  });
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [googleEventId, setGoogleEventId] = useState<string | null>(meeting?.googleEventId ?? null);
  // Documents attached while creating go with the POST; when editing they are
  // managed live on the meeting (same list the detail page shows).
  const [attachments, setAttachments] = useState<TypedAttachment[]>([]);
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ["meeting", meeting?.id],
    queryFn: async () => (await api.get<{ attachments: RelatedDocument[] }>(`/meetings/${meeting!.id}`)).data,
    enabled: open && isEdit,
  });
  // Only fetched once the organizer opts into inviting.
  const { data: directory, isLoading: loadingRecipients } = useRecipientDirectory(inviteOnCreate);
  // While the audience is untouched it tracks the meeting type; once the user
  // edits it we stop overwriting their choice.
  const [audienceTouched, setAudienceTouched] = useState(false);

  // Mint a Google Meet link through the server (Google Calendar API, domain-wide
  // delegation — see server/src/services/googleMeet.ts and .env.example).
  const genLink = useMutation({
    mutationFn: async () =>
      (
        await api.post("/meetings/generate-link", {
          title: form.title,
          scheduledAt: new Date(form.scheduledAt).toISOString(),
          ...(form.endTime ? { endTime: new Date(form.endTime).toISOString() } : {}),
        })
      ).data as { meetLink: string; eventId: string },
    onSuccess: (res) => {
      setForm((f) => ({ ...f, meetLink: res.meetLink }));
      setGoogleEventId(res.eventId || null);
    },
  });

  interface SaveResult {
    meeting: MeetingRow;
    sent?: number;
    /** Set when the meeting saved but the invitations did not go out. */
    inviteFailure?: string;
  }

  const mutation = useMutation({
    mutationFn: async (payload: typeof form): Promise<SaveResult> => {
      const scheduledAt = new Date(payload.scheduledAt).toISOString();
      const endTime = payload.endTime ? new Date(payload.endTime).toISOString() : null;
      if (isEdit && meeting) {
        const body: Record<string, unknown> = {
          title: payload.title,
          meetingType: payload.meetingType,
          scheduledAt,
          endTime,
          location: payload.location,
        };
        if (payload.meetLink) body.meetLink = payload.meetLink;
        if (googleEventId) body.googleEventId = googleEventId;
        return { meeting: (await api.put(`/meetings/${meeting.id}`, body)).data };
      }
      const created: MeetingRow = (
        await api.post("/meetings", {
          ...payload,
          scheduledAt,
          endTime,
          ...(googleEventId ? { googleEventId } : {}),
          attachments: attachments.filter((a) => a.fileUrl && a.title.trim()),
        })
      ).data;
      if (!inviteOnCreate) return { meeting: created };

      // Invitations are a second call. The meeting is already saved by now, so a
      // mail failure is reported as a warning rather than failing the whole save.
      try {
        const res = await api.post(`/meetings/${created.id}/invite`, invitePayload(selection));
        return { meeting: created, sent: (res.data as { sent: number }).sent };
      } catch (err: any) {
        return {
          meeting: created,
          inviteFailure: err?.response?.data?.message ?? t("meetings.inviteFailedAfterCreate"),
        };
      }
    },
    onSuccess: ({ meeting: saved, sent, inviteFailure }) => {
      onCreated(isEdit ? undefined : saved, sent);
      if (inviteFailure) {
        // Keep the dialog open so the organizer sees what failed; the meeting
        // itself is saved and the list behind has already refreshed.
        setInviteError(inviteFailure);
        return;
      }
      onClose();
      if (!isEdit) {
        setForm({ title: "", meetingType: "JUNTA", scheduledAt: "", endTime: "", location: "", meetLink: "" });
        setInviteOnCreate(false);
        setSelection({ ...EMPTY_SELECTION, groups: defaultGroupsFor("JUNTA") });
        setAudienceTouched(false);
        setInviteError(null);
        setGoogleEventId(null);
        setAttachments([]);
      }
    },
  });

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t("common.edit") : t("meetings.create")}>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          mutation.mutate(form);
        }}
      >
        <RulePanel tags={["meeting", "quorum", "vote"]} />
        <div>
          <label className="label">{t("meetings.title")}</label>
          <input className="input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">Tipo</label>
            <select
              className="input"
              value={form.meetingType}
              onChange={(e) => {
                const meetingType = e.target.value as MeetingRow["meetingType"];
                setForm({ ...form, meetingType });
                // Junta → the board; Asamblea/Extraordinaria → the titulares.
                if (!audienceTouched) {
                  setSelection((s) => ({ ...s, groups: defaultGroupsFor(meetingType) }));
                }
              }}
            >
              <option value="JUNTA">{t("meetings.junta")}</option>
              <option value="ASAMBLEA">{t("meetings.asamblea")}</option>
              <option value="EXTRAORDINARY">{t("meetings.extraordinary")}</option>
            </select>
          </div>
          <div>
            <label className="label">{t("meetings.scheduledAt")}</label>
            <input
              type="datetime-local"
              className="input"
              required
              value={form.scheduledAt}
              onChange={(e) => {
                const scheduledAt = e.target.value;
                // Only auto-fill the end time — never overwrite one the organizer already set.
                setForm((f) => ({ ...f, scheduledAt, endTime: f.endTime || suggestEndTime(scheduledAt) }));
              }}
            />
          </div>
        </div>
        <div>
          <label className="label">{t("meetings.endTime")}</label>
          <input
            type="datetime-local"
            className="input"
            required
            min={form.scheduledAt || undefined}
            value={form.endTime}
            onChange={(e) => setForm({ ...form, endTime: e.target.value })}
          />
          {form.scheduledAt && form.endTime && new Date(form.endTime) <= new Date(form.scheduledAt) && (
            <p className="text-xs text-red-600 mt-1">{t("meetings.endTimeBeforeStart")}</p>
          )}
        </div>
        <div>
          <label className="label">{t("meetings.location")}</label>
          <input className="input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
        </div>
        <div>
          <label className="label">{t("meetings.meetLink")}</label>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="https://meet.google.com/..."
              value={form.meetLink}
              onChange={(e) => setForm({ ...form, meetLink: e.target.value })}
            />
            <button
              type="button"
              className="btn-secondary whitespace-nowrap"
              disabled={!form.title || !form.scheduledAt || genLink.isPending}
              title={!form.title || !form.scheduledAt ? t("meetings.fillTitleFirst") : undefined}
              onClick={() => genLink.mutate()}
            >
              {genLink.isPending ? t("meetings.generatingLink") : t("meetings.generateMeetLink")}
            </button>
          </div>
          {(!form.title || !form.scheduledAt) && (
            <p className="text-xs text-slate-400 mt-1">{t("meetings.fillTitleFirst")}</p>
          )}
          {genLink.isError && (
            <p className="text-xs text-red-600 mt-1">
              {(genLink.error as any)?.response?.data?.message ?? t("meetings.meetNotConfigured")}
            </p>
          )}
        </div>
        <div className="border-t border-slate-200 pt-3">
          <div className="text-sm font-medium flex items-center gap-1.5 mb-2">
            <Paperclip size={14} /> {t("meetings.attachments")}
          </div>
          {isEdit && meeting ? (
            <RelatedDocuments
              endpoint={`/meetings/${meeting.id}/attachments`}
              items={detail?.attachments ?? []}
              kinds={MEETING_ATTACHMENT_KINDS}
              kindLabel={(k) => t(`meetings.attachmentKind.${k}`)}
              defaultKind="CONVOCATORIA"
              folder="costadorada1/minutes"
              canEdit
              canDelete={user?.role === "ADMIN"}
              onChanged={() => {
                qc.invalidateQueries({ queryKey: ["meeting", meeting.id] });
                qc.invalidateQueries({ queryKey: ["meetings"] });
              }}
              emptyText={t("meetings.noAttachments")}
              highlightKinds={["MINUTES_FILE"]}
              noteForKind={(k) => (k === "MINUTES_FILE" ? t("meetings.minutesFileNote") : null)}
            />
          ) : (
            <TypedAttachmentField
              label={t("meetings.attachmentsOnCreate")}
              folder="costadorada1/minutes"
              value={attachments}
              onChange={setAttachments}
              kinds={MEETING_ATTACHMENT_KINDS}
              kindLabel={(k) => t(`meetings.attachmentKind.${k}`)}
              defaultKind="CONVOCATORIA"
              allowLink
            />
          )}
        </div>
        {!isEdit && (
          <div className="border-t border-slate-200 pt-3 space-y-3">
            <label className="text-sm flex items-center gap-2 font-medium">
              <input type="checkbox" checked={inviteOnCreate} onChange={(e) => setInviteOnCreate(e.target.checked)} />
              {t("meetings.inviteOnCreate")}
            </label>
            {inviteOnCreate && (
              <MeetingRecipientPicker
                value={selection}
                onChange={(next) => {
                  setAudienceTouched(true);
                  setSelection(next);
                }}
                data={directory}
                isLoading={loadingRecipients}
              />
            )}
          </div>
        )}
        {inviteError && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            {t("meetings.meetingSavedInviteFailed")} {inviteError}
          </div>
        )}
        {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
          <button
            type="submit"
            className="btn-primary"
            disabled={
              mutation.isPending ||
              (!isEdit && inviteOnCreate && selectionCount(selection, directory) === 0) ||
              (!!form.scheduledAt && !!form.endTime && new Date(form.endTime) <= new Date(form.scheduledAt))
            }
          >
            {mutation.isPending && inviteOnCreate ? t("meetings.sendingInvites") : t("common.save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function InviteModal({ meeting, onClose }: { meeting: MeetingRow; onClose: () => void }) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<InviteSelection>({
    ...EMPTY_SELECTION,
    groups: defaultGroupsFor(meeting.meetingType),
  });
  const [sent, setSent] = useState<number | null>(null);

  const { data, isLoading } = useRecipientDirectory();
  const empty = data && Object.values(data).every((list) => !list.length);

  const mutation = useMutation({
    mutationFn: async () =>
      (await api.post(`/meetings/${meeting.id}/invite`, invitePayload(selection))).data,
    onSuccess: (res: { sent: number }) => setSent(res.sent),
  });

  const total = selectionCount(selection, data);
  return (
    <Modal open onClose={onClose} title={`${t("meetings.inviteTitle")} — ${meeting.title}`}>
      {sent !== null ? (
        <div className="space-y-4">
          <div className="text-sm text-green-700">{t("meetings.invitesSent", { count: sent })}</div>
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={onClose}>{t("common.close")}</button>
          </div>
        </div>
      ) : isLoading ? (
        <div className="text-slate-500 text-sm">{t("common.loading")}</div>
      ) : empty ? (
        <div className="text-slate-500 text-sm">{t("meetings.noRecipients")}</div>
      ) : (
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }}>
          <MeetingRecipientPicker value={selection} onChange={setSelection} data={data} />
          {mutation.isError && <div className="text-sm text-red-600">{(mutation.error as any)?.response?.data?.message ?? t("common.error")}</div>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" disabled={total === 0 || mutation.isPending}>
              <Mail size={16} /> {mutation.isPending ? t("meetings.sendingInvites") : t("meetings.sendInvites")}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
